import { Reply } from '@/models/registry';
import type { Reply as ReplyType } from '@/models/types';
import { makeAutoObservable, observable, type ObservableMap } from 'mobx';
import { db } from '../db';
import type { DeltaPacketRecord } from '../hydrate';
import { hydrateFromWorker } from '../hydrate-worker-utils';
import type { BaseStore } from './base';

class ReplyStore implements BaseStore<Reply, ReplyType> {
  store: ObservableMap<string, Reply> = observable.map();

  // Index: comment_id -> array of replies
  storeIndexedByForeignKey: ObservableMap<string, Reply[]> = observable.map();

  constructor() {
    makeAutoObservable(
      this,
      {
        store: observable.shallow,
        storeIndexedByForeignKey: observable.shallow,
      },
      { autoBind: true },
    );
  }

  private getOrCreateCollection(commentId: string): Reply[] {
    let arr = this.storeIndexedByForeignKey.get(commentId);
    if (!arr) {
      arr = observable.array<Reply>([], { deep: false });
      this.storeIndexedByForeignKey.set(commentId, arr);
    }
    return arr;
  }

  private setReplyField = <K extends keyof ReplyType>(
    reply: Reply,
    key: K,
    value: ReplyType[K],
  ) => {
    (reply as Pick<Reply, K>)[key] = value as Reply[K];
  };

  replaceOnlyUpdatedFields({ delta }: { delta: DeltaPacketRecord<ReplyType> }) {
    const existingReply = this.getById(delta.record_id);
    if (!existingReply) return;

    let changed = false;

    const entries = Object.entries(delta.data) as [
      keyof ReplyType,
      ReplyType[keyof ReplyType],
    ][];

    for (const [key, newValue] of entries) {
      if (newValue === undefined || existingReply[key] === newValue) {
        continue;
      }

      this.setReplyField(existingReply, key, newValue);
      changed = true;
    }

    if (!changed) return;
    this.save(existingReply);
  }

  applyDeltas({
    deltas,
    hydrateIndexedDB,
  }: {
    deltas: DeltaPacketRecord<ReplyType>[];
    hydrateIndexedDB?: boolean;
  }) {
    for (const delta of deltas) {
      this.applyDelta({ delta, hydrateIndexedDB });
    }
  }

  applyDeltaV2({
    delta,
    hydrateIndexedDB,
  }: {
    delta: DeltaPacketRecord<ReplyType>;
    hydrateIndexedDB?: boolean;
  }) {
    if (delta.action === 'delete' || delta.action === 'archive') {
      if (hydrateIndexedDB) {
        db.comment_replies.delete(delta.record_id);
      }
      this.remove(delta.record_id);
      return;
    }

    if (delta.action === 'create') {
      if (hydrateIndexedDB) {
        db.comment_replies.put(delta.data, delta.record_id);
      }
      this.upsert(delta.data);
      return;
    }

    if (delta.action === 'update') {
      if (hydrateIndexedDB) {
        db.comment_replies.update(delta.record_id, delta.data);
      }
      this.replaceOnlyUpdatedFields({ delta });
      return;
    }

    if (hydrateIndexedDB) {
      db.comment_replies.put(delta.data, delta.record_id);
    }
    this.upsert(delta.data);
  }

  applyDelta({
    delta,
    hydrateIndexedDB,
  }: {
    delta: DeltaPacketRecord<ReplyType>;
    hydrateIndexedDB?: boolean;
  }) {
    if (hydrateIndexedDB) {
      db.comment_replies.put(delta.data, delta.record_id);
    }

    if (delta.action === 'delete') {
      this.store.delete(delta.record_id);
      return;
    }

    if (delta.action === 'archive') {
      this.remove(delta.record_id);
      return;
    }

    this.upsert(delta.data);
  }

  getById(id: string): Reply | undefined {
    return this.store.get(id);
  }

  add(input: ReplyType) {
    const reply = new Reply(input);
    this.store.set(reply.id, reply);
    // Index by foreign key for O(1) lookups
    this.getOrCreateCollection(reply.comment_id).push(reply);
  }

  save(reply: Reply) {
    this.store.set(reply.id, reply);
    // Index by foreign key for O(1) lookups
    const arr = this.getOrCreateCollection(reply.comment_id);
    const i = arr.findIndex((r) => r.id === reply.id);
    if (i >= 0) arr.splice(i, 1, reply);
    else arr.push(reply);
  }

  upsert(reply: ReplyType) {
    const existingReply = this.getById(reply.id);
    if (existingReply) {
      this.replace({ id: reply.id, reply });
    } else {
      this.add(reply);
    }
  }

  remove(id: ReplyType['id']) {
    const existingReply = this.getById(id);
    if (!existingReply) return;

    this.store.delete(id);

    // remove from the index
    const arr = this.getOrCreateCollection(existingReply.comment_id);
    const i = arr.findIndex((r) => r.id === id);
    if (i >= 0) arr.splice(i, 1);

    // remove from dexie
    db.comment_replies.delete(id);
  }

  replace({ id, reply }: { id: string; reply: ReplyType }) {
    const existingReply = this.getById(id);
    if (!existingReply) return;

    existingReply.content = reply.content;
    existingReply.edited = reply.edited;
    existingReply.updated_at = reply.updated_at;
    existingReply.text = reply.text;
    existingReply.mentions = reply.mentions;
    this.save(existingReply);
  }

  // TODO - table and entity name should prob be generic replies?
  hydrateLocal(): Promise<void> {
    return hydrateFromWorker({
      tableName: 'deal_replies_v3',
      entity: 'deal_replies_v3',
      modelStore: replyStore,
    });
  }

  async hydrateDeal(commentIds: ReplyType['comment_id'][]): Promise<void> {
    const items = await db.comment_replies.toArray();
    items.forEach((item) => {
      if (commentIds.includes(item.comment_id)) this.upsert(item);
    });
  }
}

export const replyStore = new ReplyStore();
